// $FlowFixMe
import React, { useEffect, useState, useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Col, Row } from 'react-bootstrap';
import { Field, change } from 'redux-form';
import { pathOr } from 'ramda';
import styled from 'styled-components';
import { renderSelectField } from '../../../../../utils/redux-form-helper';

import {
  ELECTRONIC_SIGNING_METHOD,
  generateLeaseSigningOptions,
  MANUAL_SIGNING_METHOD,
  signingMethodToRestore,
} from '../../../../../utils/lease-helpers';

import messages from './messages';

const PaddedP = styled.p`
  padding: 10px;
  margin: 10px;
`;

type Props = {
  hasNonFR: boolean,
  intl: Object,
  isEditable: boolean,
  lease: Object,
  selectedProperty: Object,
  leaseProvider?: 'BLUEMOON' | 'FORTRESS' | 'UNKNOWN',
  formSigMethod?: ?string,
  initialSigMethod?: ?string,
  onRestoreSigMethod?: (method: string) => void,
};

export const LeaseSigningMethod = ({
  hasNonFR,
  intl,
  isEditable,
  lease,
  selectedProperty,
  leaseProvider,
  formSigMethod,
  initialSigMethod,
  onRestoreSigMethod,
}: Props) => {
  const [sigMethod, setSigMethod] = useState(
    lease.desiredSignatureMethod || null,
  );
  const hasDocument = pathOr(false, ['documentId'], lease);

  const { iliElectronicLease: isIliElectronicSigningFlagOn } = useFlags();

  const isIli = useMemo(() => {
    const orgName = selectedProperty?.organization?.name;
    return orgName === 'Independent Living';
  }, [selectedProperty]);

  const updateSignatureMethodNotes = (e) => {
    change('FormLeaseDataTab', 'desiredSignatureMethod', e.target.value);
    setSigMethod(e.target.value);
  };

  const isResidentPortalActive = pathOr(
    false,
    ['isResidentPortalActive'],
    selectedProperty,
  );
  const isElectronicLeaseSigningActive = pathOr(
    false,
    ['isElectronicLeaseSigningActive'],
    selectedProperty,
  );

  // No provider note while the provider is unresolved: either note could be wrong.
  const showProviderNote = leaseProvider !== 'UNKNOWN';
  const showProviderUnknownNote =
    leaseProvider === 'UNKNOWN' &&
    isEditable &&
    !hasNonFR &&
    lease.desiredSignatureMethod !== ELECTRONIC_SIGNING_METHOD;

  const options = useMemo(
    () =>
      generateLeaseSigningOptions({
        isResidentPortalActive,
        isElectronicLeaseSigningActive,
        hasNonFR,
        isEditable,
        sigMethod,
        isIli,
        isIliElectronicSigningFlagOn,
        leaseProvider,
        savedSigMethod: lease.desiredSignatureMethod,
        labels: {
          blueMoonElectronic: intl.formatMessage(
            messages.blueMoonElectronicOption,
          ),
          electronic: intl.formatMessage(messages.electronicOption),
        },
      }),
    [
      isResidentPortalActive,
      isElectronicLeaseSigningActive,
      hasNonFR,
      isEditable,
      sigMethod,
      isIli,
      isIliElectronicSigningFlagOn,
      leaseProvider,
      lease.desiredSignatureMethod,
      intl,
    ],
  );

  // Every submission path sends the whole form, so an unsaved choice the options no longer allow
  // reverts here rather than being blocked at each submit.
  useEffect(() => {
    if (!onRestoreSigMethod) return;
    const restored = signingMethodToRestore({
      options,
      formSigMethod,
      initialSigMethod,
    });
    if (restored) {
      onRestoreSigMethod(restored);
      setSigMethod(restored);
    }
  }, [options, formSigMethod, initialSigMethod, onRestoreSigMethod]);

  return (
    <div>
      <Row className="padbottom10">
        <Col xs={12}>
          <h2 className="padtop20 required">
            <i className="et-checklist text-blue padright10" />
            <FormattedMessage {...messages.leaseSigningMethod} />
          </h2>
        </Col>
        {hasNonFR && (
          <PaddedP className="highlight-details">
            <strong>{intl.formatMessage(messages.noteOnSigningMethod)}</strong>
            {intl.formatMessage(messages.lockedNonFinancial)}
          </PaddedP>
        )}
        <Col xs={12}>
          <Row>
            <Col xs={12} className="padleft20">
              {lease.desiredSignatureMethod === 'default' ? (
                <p>
                  <FormattedMessage {...messages.note} />
                </p>
              ) : null}
            </Col>
          </Row>
          <Row className="padtop10">
            <Col xs={3} className="text-right">
              <label>
                <FormattedMessage {...messages.signingMethod} />
              </label>
            </Col>
            <Col xs={4}>
              <Field
                name="desiredSignatureMethod"
                component={renderSelectField}
                options={options}
                onChange={updateSignatureMethodNotes}
                disabled={!isEditable}
              />
            </Col>
          </Row>
          <Row>
            {!hasDocument && (
              <Col xs={12}>
                {showProviderUnknownNote && (
                  <p>{intl.formatMessage(messages.providerUnknownNote)}</p>
                )}
                {showProviderNote &&
                  sigMethod === MANUAL_SIGNING_METHOD &&
                  lease.desiredSignatureMethod !== 'default' && (
                    <p>
                      <FormattedMessage
                        {...(leaseProvider === 'BLUEMOON'
                          ? messages.blueMoonManualNote
                          : messages.manualNote)}
                      />
                    </p>
                  )}
                {showProviderNote &&
                  sigMethod === ELECTRONIC_SIGNING_METHOD &&
                  lease.desiredSignatureMethod !== 'default' && (
                    <p>
                      <FormattedMessage
                        {...(leaseProvider === 'BLUEMOON'
                          ? messages.blueMoonElectronicNote
                          : messages.electronicNote)}
                      />
                    </p>
                  )}
              </Col>
            )}
          </Row>
        </Col>
      </Row>
    </div>
  );
};

export default injectIntl(LeaseSigningMethod);
